import {Component, computed, DestroyRef, inject, Injector, input, signal, ChangeDetectionStrategy} from "@angular/core"
import {CM_LOGGER_APP} from "@app/common/services/logging"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {MatDialog, MatDialogRef} from "@angular/material/dialog"
import {MatMenuModule} from "@angular/material/menu"
import {MatProgressBarModule} from "@angular/material/progress-bar"
import {MatSnackBar} from "@angular/material/snack-bar"
import {MatTooltipModule} from "@angular/material/tooltip"
import {NextActor, PaymentState} from "@generated"
import {ListInfoTagFragment, ListInfoUserFragment} from "@app/common/components/item/list-info/list-info.generated"
import {initializeMenuItems, MenuItem} from "@app/common/components/menu/multi-level-menu/menu-item"
import {MultiLevelMenu} from "@app/common/components/menu/multi-level-menu/multi-level-menu.component"
import {BatchDownloadOperation} from "@app/common/models/background-operation/batch-download-operation"
import {NotificationsService} from "@app/common/services/notifications/notifications.service"
import {OrganizationsService} from "@app/common/services/organizations/organizations.service"
import {BackgroundOperationService} from "@app/platform/services/background-operation/background-operation.service"
import {IsDefined, IsNonNull} from "@cm/utils"
import {DialogComponent} from "@common/components/dialogs/dialog/dialog.component"
import {ListInfoOrganizationLegacyIdGQL, ListInfoVisibleTagsGQL, ListInfoVisibleUsersGQL} from "@common/components/item/list-info/list-info.generated"
import {PlaceholderComponent} from "@common/components/placeholders/placeholder/placeholder.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {GenericItemList} from "@common/models/item/generic-item-list"
import {
    AdvancedAction,
    BatchDownloadData,
    BatchUpdateProperty,
    emptyBatchEditInfo,
    ExtraBatchActionItem,
    isExtraBatchActionItemWithSubItems,
    SortCriterion,
} from "@common/models/item/list-item"
import {AuthService} from "@common/services/auth/auth.service"
import {Labels, StateLabel} from "@labels"
import {firstValueFrom} from "rxjs"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-list-info",
    templateUrl: "./list-info.component.html",
    styleUrls: ["./list-info.component.scss"],
    imports: [MatTooltipModule, MatMenuModule, MatProgressBarModule, MultiLevelMenu, PlaceholderComponent],
})
export class ListInfoComponent<EntityType extends {id: string; legacyId: number}> {
    private readonly logger = inject(CM_LOGGER_APP)
    readonly $count = input<number | undefined>(undefined, {alias: "count"})
    readonly $parentList = input<GenericItemList<EntityType> | undefined>(undefined, {alias: "parentList"})
    readonly $stateLabels = input<StateLabel<string>[]>([], {alias: "stateLabels"})
    readonly $allowStandardUpdates = input<boolean>(false, {alias: "allowStandardUpdates"}) //Updates, that are always available: state, payment state, up next, assign user, add/remove tag
    readonly $extraBatchActions = input<ExtraBatchActionItem<EntityType>[]>([], {alias: "extraBatchActions"})
    readonly $advancedActions = input<AdvancedAction[]>([], {alias: "advancedActions"})
    readonly $sortCriteria = input<SortCriterion[]>([], {alias: "sortCriteria"})
    readonly $batchDownloadData = input<BatchDownloadData<EntityType>[]>([], {alias: "batchDownloadData"})
    readonly $createItem = input<(() => void) | undefined>(undefined, {alias: "createItem"})

    readonly $downloadMenuItems = computed(() => initializeMenuItems(this.$batchDownloadData()))
    readonly $showBatchButton = computed(() => {
        return this.$allowStandardUpdates() || this.$extraBatchActions().length > 0 || this.$batchDownloadData().length > 0
    })

    readonly injector = inject(Injector)
    readonly organizations = inject(OrganizationsService)

    readonly $maxBatchEditNr = signal(200)
    readonly $maxBatchDownloadNr = signal(this.$maxBatchEditNr())
    readonly $paymentStateLabels = signal<StateLabel<PaymentState>[]>(Array.from(Labels.PaymentState.values()))
    readonly $nextActorLabels = signal<StateLabel<NextActor>[]>(Array.from(Labels.NextActor.values()))
    readonly $batchEditInfo = signal(emptyBatchEditInfo)
    readonly $visibleTags = signal<ListInfoTagFragment[] | null | undefined>(null)
    readonly $visibleUsers = signal<ListInfoUserFragment[] | null | undefined>(null)

    private readonly listInfoOrganizationLegacyId = inject(ListInfoOrganizationLegacyIdGQL)
    private readonly visibleTagsGql = inject(ListInfoVisibleTagsGQL)
    private readonly visibleUsersGql = inject(ListInfoVisibleUsersGQL)
    private readonly snackBar = inject(MatSnackBar)
    private readonly dialog = inject(MatDialog)
    protected readonly destroyRef = inject(DestroyRef)
    protected readonly organizationsService = inject(OrganizationsService)
    protected readonly backgroundOperationService = inject(BackgroundOperationService)
    protected readonly notifications = inject(NotificationsService)
    protected readonly authService = inject(AuthService)
    loadUsersAndTags = () => {
        if (this.$visibleTags() === null) {
            this.$visibleTags.set(undefined)
            fetchThrowingErrors(this.visibleTagsGql)({
                filter: this.authService.isStaff() ? {} : {organizationId: {in: this.organizations.$own()?.map(({id}) => id)}},
            })
                .then(({tags}) => {
                    this.$visibleTags.set(tags.filter(IsDefined))
                })
                .catch((error) => {
                    this.$visibleTags.set(null)
                    throw error
                })
        }
        if (this.$visibleUsers() === null) {
            this.$visibleUsers.set(undefined)
            fetchThrowingErrors(this.visibleUsersGql)({})
                .then(({users}) => {
                    this.$visibleUsers.set(users.filter(IsDefined))
                })
                .catch((error) => {
                    this.$visibleUsers.set(null)
                    throw error
                })
        }
    }

    public batchUpdate = async (property: BatchUpdateProperty, value: string | boolean) => {
        const parentList = this.$parentList()
        if (!parentList || !parentList.batchUpdate) throw Error("No parentList or batchUpdate function is set")

        const count = this.$count() ?? 0
        const confirmed = await this.confirmBatchEdit(count)
        if (confirmed) return parentList.batchUpdate(property, value)
        else return undefined
    }

    private collectDownloadedDataObjects = async (
        data: BatchDownloadData<EntityType>,
    ): Promise<{downloadedDataObjects: Map<string, number>; missingItemsLegacyIds: number[]}> => {
        const downloadedDataObjects = new Map<string, number>()
        const missingItemsLegacyIds: number[] = []
        const nameCounters = new Map<string, number>()

        const batchItemsResult = await this.$parentList()?.getItems(0, this.$maxBatchEditNr())
        const batchItems = batchItemsResult ? batchItemsResult.items : []

        const promises = batchItems.filter(IsNonNull).map((item) =>
            data
                .getDataObjectsForDownload(item)
                .then((paths) => {
                    paths.forEach(({path, dataObjectLegacyId}) => {
                        let uniquePath = path
                        if (downloadedDataObjects.has(path)) {
                            const count = (nameCounters.get(path) || 0) + 1
                            nameCounters.set(path, count)
                            const countPadded = String(count).padStart(3, "0")
                            const dotIndex = path.lastIndexOf(".")
                            const baseName = dotIndex > 0 ? path.substring(0, dotIndex) : path
                            const extension = dotIndex > 0 ? path.substring(dotIndex) : ""
                            uniquePath = `${baseName}-${countPadded}${extension}`
                        } else nameCounters.set(path, 0)

                        downloadedDataObjects.set(uniquePath, dataObjectLegacyId)
                    })
                })
                .catch((error) => {
                    missingItemsLegacyIds.push(item.legacyId)
                    this.logger.debug("Could not get data for download:", error)
                }),
        )

        await Promise.allSettled(promises)

        return {downloadedDataObjects, missingItemsLegacyIds}
    }

    public startDownload = async (menuItem: MenuItem) => {
        const data = menuItem.context as BatchDownloadData<EntityType>

        const {downloadedDataObjects, missingItemsLegacyIds} = await this.collectDownloadedDataObjects(data)
        if (downloadedDataObjects.size === 0) {
            this.notifications.showError("No data for download found. Was everything created?")
            return
        }

        const confirmed = await this.confirmBatchDownload(downloadedDataObjects.size, missingItemsLegacyIds)
        if (!confirmed) return

        const organizationDetails = await this.organizationsService.current
        if (!organizationDetails) throw new Error("Organization not found")

        const description = data.pathInMenu.replace(/\//g, ", ")

        const organizationWithLegacyId = await fetchThrowingErrors(this.listInfoOrganizationLegacyId)({id: organizationDetails.id})

        const downloadOperation = new BatchDownloadOperation(
            this.injector,
            description,
            description,
            organizationWithLegacyId.organization.legacyId,
            downloadedDataObjects,
        )
        this.backgroundOperationService.registerBackgroundOperation(downloadOperation)

        void downloadOperation.startOperation()
    }

    private confirmBatchDownload = (foundItems: number, missingItemsLegacyIds: number[]) => {
        const missingItems = missingItemsLegacyIds.length
        const dialogRef: MatDialogRef<DialogComponent, boolean> = this.dialog.open(DialogComponent, {
            disableClose: false,
            width: "400px",
            data: {
                title: "Batch download",
                message:
                    missingItems === 0
                        ? `You are downloading ${foundItems} items.`
                        : `Not all data available. Do you want to download ${foundItems} of ${missingItems + foundItems} items?<br><br>Missing items:<br>${missingItemsLegacyIds.map((id) => `• ${id}`).join("<br>")}`,
                confirmLabel: "Submit",
                cancelLabel: "Cancel",
            },
        })
        return firstValueFrom(dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)))
    }

    private confirmBatchEdit = (count: number) => {
        const dialogRef: MatDialogRef<DialogComponent, boolean> = this.dialog.open(DialogComponent, {
            disableClose: false,
            width: "400px",
            data: {
                title: "Batch edit",
                message: `You are submitting ${count} edits.<br>Are you sure you want to continue?`,
                confirmLabel: "Submit",
                cancelLabel: "Cancel",
            },
        })
        return firstValueFrom(dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)))
    }

    getBatchItems = async () => {
        const count = this.$count()
        if (count !== undefined && count <= this.$maxBatchEditNr()) {
            const confirmed = await this.confirmBatchEdit(count)
            if (confirmed) {
                const parentList = this.$parentList()
                if (!parentList) throw Error("No parentList is set")
                return (await parentList.getItems(0, this.$maxBatchEditNr())).items
            }
        }

        return undefined
    }

    batchProcessSingleItems = async (operation: (item: EntityType) => Promise<boolean>, text?: string) => {
        if (this.$batchEditInfo().running) throw new Error("Batch edit already running.")

        const parentList = this.$parentList()
        if (!parentList) throw Error("No parentList is set")

        this.$batchEditInfo.set({
            ...emptyBatchEditInfo,
            running: true,
        })

        try {
            const batchItems = await this.getBatchItems()

            if (batchItems === undefined) {
                return
            }

            this.$batchEditInfo.set({
                ...this.$batchEditInfo(),
                total: batchItems.length,
            })

            for (const item of batchItems.filter(IsNonNull)) {
                try {
                    const result = await operation(item)
                    if (result === false)
                        this.$batchEditInfo.set({
                            ...this.$batchEditInfo(),
                            numSkipped: this.$batchEditInfo().numSkipped + 1,
                        })
                    else {
                        this.$batchEditInfo.set({
                            ...this.$batchEditInfo(),
                            numSucceeded: this.$batchEditInfo().numSucceeded + 1,
                        })
                        await parentList.refreshItem(item)
                    }
                } catch (err) {
                    this.logger.error(`Failed batch item:`, {item, error: err})
                    this.$batchEditInfo.set({
                        ...this.$batchEditInfo(),
                        numErrored: this.$batchEditInfo().numErrored + 1,
                    })
                }
            }

            const {numSucceeded, numSkipped, numErrored} = this.$batchEditInfo()
            const snackBarText = `${numSucceeded} ${text ?? "changes saved"}${numSkipped > 0 ? ` (${numSkipped} skipped)` : ""}.${
                numErrored > 0 ? ` ${numErrored} failed (see console for details).` : ""
            }`
            this.snackBar.open(snackBarText, numErrored > 0 ? "Dismiss" : "", numErrored > 0 ? undefined : {duration: 3000})
        } finally {
            this.$batchEditInfo.set(emptyBatchEditInfo)
        }
    }

    readonly Labels = Labels
    readonly $isExtraBatchActionItemWithSubItems = signal(isExtraBatchActionItemWithSubItems)
}
